import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useHome from './dashboard/useHome';
import DashboardView from './dashboard/DashboardView';
import ServicesManagerDialog from '../components/services/ServicesManagerDialog';
import ContextualHint from '../components/ContextualHint';
import { useNotificationCenter } from '../contexts/NotificationCenterContext';
import { useContextualHint } from '../hooks/useContextualHint';
import { useUpdateOnboardingStats } from '../hooks/useOnboarding';
import dashboardService from '../services/dashboardService';

/**
 * A HOME do Admin — container.
 *
 * Toda a decisão de o que aparece vem de `GET /api/v1/home` (decisão D2 do redesenho): ordem,
 * peso, estado e o resumo de cada escopo chegam prontos. Aqui não existe `&&` decidindo se um
 * card existe — se voltar a existir, a decisão foi quebrada e ajustar a Home volta a exigir
 * deploy do front.
 *
 * O desenho é da `DashboardView` (Claude Design). Este arquivo só liga os fios.
 *
 * ⚠️ Sem `PageLayout`: a View desenha a página inteira, incluindo a saudação do topo. Envolver
 * em PageLayout duplicaria o cabeçalho — é o mesmo padrão de Ausências e Suporte.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { vm, actions, registrarEvento } = useHome();
  const { openNotificationCenter } = useNotificationCenter();
  const [servicesManagerOpen, setServicesManagerOpen] = useState(false);

  /**
   * `/dashboard/stats` continua sendo carregado — e NÃO é sobra de refactor.
   *
   * Quem depende do retrato CRU da conta é o `useUpdateOnboardingStats`: esta página é o ÚNICO
   * alimentador do contexto de onboarding, e ele precisa dos números sem o recorte da Home.
   *
   * Mover este sinal para o contrato da Home é dívida registrada, não faxina para agora.
   */
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    dashboardService.getStats()
      .then((dados) => { if (vivo) setStats(dados); })
      // O catch fica: sem ele a promessa rejeitada vira unhandled rejection.
      .catch(() => { /* stats fica null — cada card decide o que mostrar sem ele */ })
      .finally(() => { if (vivo) setStatsLoading(false); });
    return () => { vivo = false; };
  }, []);

  useUpdateOnboardingStats(stats, statsLoading);
  const hint = useContextualHint('dashboard', { stats });

  /**
   * Os dois eventos locais do contrato (`action.type === 'emit'`).
   *
   * Existem porque nem todo clique é navegação: o contador de Serviços abre um diálogo e "Ver
   * todos" os avisos abre o painel lateral. Sem `emit`, os dois virariam rota — mudando o
   * produto por limitação do contrato.
   */
  useEffect(() => {
    registrarEvento('openServicesManager', () => setServicesManagerOpen(true));
    registrarEvento('openNotifications', () => openNotificationCenter());
  }, [registrarEvento, openNotificationCenter]);

  return (
    <>
      {hint && (
        <ContextualHint
          hint={hint}
          onActionClick={hint?.action?.link ? () => navigate(hint.action.link) : undefined}
        />
      )}

      <DashboardView vm={vm} actions={actions} />

      <ServicesManagerDialog
        open={servicesManagerOpen}
        onClose={() => setServicesManagerOpen(false)}
      />
    </>
  );
}
